import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { penaltyAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Gavel, Plus, Search, Loader2 } from 'lucide-react';
import PenaltyTable from '@/components/penalties/PenaltyTable';
import PenaltyFormDialog from '@/components/penalties/PenaltyFormDialog';
import PenaltyDeductDialog from '@/components/penalties/PenaltyDeductDialog';
import PenaltyRevertDialog from '@/components/penalties/PenaltyRevertDialog';
import PenaltyStats from '@/components/penalties/PenaltyStats';

export default function PenaltiesPage() {
  const { user } = useAuth();
  const canDeduct = ['super_admin', 'admin', 'payroll_auditor'].includes(user?.role);
  const canRevert = ['super_admin', 'admin'].includes(user?.role);

  const [penalties, setPenalties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deductTarget, setDeductTarget] = useState(null);
  const [deductMode, setDeductMode] = useState('salary');
  const [revertTarget, setRevertTarget] = useState(null);
  const [waiveTarget, setWaiveTarget] = useState(null);
  const [waiveReason, setWaiveReason] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const [listRes, statsRes] = await Promise.all([penaltyAPI.getAll(params), penaltyAPI.getStats()]);
      setPenalties(listRes.data.penalties || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to load penalties');
    }
    setLoading(false);
  }, [statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchData, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchData, search]);

  const handleAcknowledge = async (p) => {
    try {
      await penaltyAPI.update(p.id, { status: 'acknowledged' });
      toast.success('Penalty acknowledged');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleWaive = async () => {
    try {
      await penaltyAPI.update(waiveTarget.id, { status: 'waived', waive_reason: waiveReason || null });
      toast.success('Penalty waived');
      setWaiveTarget(null);
      setWaiveReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to waive');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete penalty for ${p.employee_name || 'employee'}?`)) return;
    try {
      await penaltyAPI.delete(p.id);
      toast.success('Penalty deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="penalties-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Gavel className="h-6 w-6" />Penalties
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">Track disciplinary shortcomings and salary recoveries</p>
          </div>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-penalty-btn">
            <Plus className="h-4 w-4 mr-2" />Record Penalty
          </Button>
        </div>

        {/* Stats */}
        <PenaltyStats stats={stats} />

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search employee, code, category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="penalty-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="penalty-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="deducted">Deducted</SelectItem>
              <SelectItem value="waived">Waived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <PenaltyTable
                penalties={penalties}
                canDeduct={canDeduct}
                canRevert={canRevert}
                onAcknowledge={handleAcknowledge}
                onDeduct={(p, mode) => { setDeductMode(mode || 'salary'); setDeductTarget(p); }}
                onRevert={setRevertTarget}
                onWaive={(p) => { setWaiveTarget(p); setWaiveReason(''); }}
                onEdit={(p) => { setEditing(p); setFormOpen(true); }}
                onDelete={handleDelete}
              />
            )}
          </CardContent>
        </Card>

        {/* Dialogs */}
        <PenaltyFormDialog open={formOpen} onOpenChange={setFormOpen} penalty={editing} onSaved={fetchData} />
        {deductTarget && (
          <PenaltyDeductDialog penalty={deductTarget} mode={deductMode} onOpenChange={(v) => !v && setDeductTarget(null)} onDeducted={fetchData} />
        )}
        {revertTarget && (
          <PenaltyRevertDialog penalty={revertTarget} onOpenChange={(v) => !v && setRevertTarget(null)} onReverted={fetchData} />
        )}

        <Dialog open={!!waiveTarget} onOpenChange={(v) => !v && setWaiveTarget(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="waive-dialog">
            <DialogHeader>
              <DialogTitle>Waive Penalty</DialogTitle>
              <DialogDescription>
                Waive the penalty for {waiveTarget?.employee_name}? It won't be deducted from salary.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Reason for waiving (optional)"
              value={waiveReason}
              onChange={e => setWaiveReason(e.target.value)}
              rows={2}
              data-testid="waive-reason-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setWaiveTarget(null)}>Cancel</Button>
              <Button onClick={handleWaive} data-testid="waive-confirm-btn">Waive Penalty</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
